/**
 * The garage switcher and the create-garage flow (§8).
 *
 * A user can own or crew several garages, so the switcher is the tenant
 * selector for the whole app — everything below it re-queries when this
 * changes.
 */

import { useState } from 'react';
import { Check, ChevronDown, Globe, Plus, Users } from 'lucide-react';

import { useCreateGarage } from '../api/hooks';
import { ApiError } from '../api/client';
import { errorMessage } from '../components/states';
import { useGarageContext } from './GarageProvider';

export function GarageSwitcher() {
  const { garages, garage, select } = useGarageContext();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  if (!garage) return null;

  return (
    <>
      <div className="gsw">
        <button className="gsw-btn" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu">
          <span className="gsw-name">{garage.name}</span>
          {/* The at-a-glance public marker §11 asks for, on the one control
              that is visible from every screen in the app. */}
          {garage.isPublic && (
            <span className="gsw-public" title="This garage is public">
              <Globe size={11} />
            </span>
          )}
          <span className={'gsw-role role-' + garage.role}>{garage.role}</span>
          <ChevronDown size={14} />
        </button>

        {open && (
          <>
            {/* Click-away, as a sibling rather than a document listener: no
                effect to leak, and it cannot fight the button's own onClick. */}
            <div className="gsw-scrim" onClick={() => setOpen(false)} />
            <div className="gsw-menu" role="menu">
              {garages.map((g) => (
                <button
                  key={g.id}
                  role="menuitem"
                  className={'gsw-item' + (g.id === garage.id ? ' gsw-item-on' : '')}
                  onClick={() => {
                    select(g.id);
                    setOpen(false);
                  }}
                >
                  <span className="gsw-item-name">
                    {g.name}
                    {g.isPublic && <Globe size={10} className="gsw-item-globe" />}
                  </span>
                  <span className={'gsw-role role-' + g.role}>{g.role}</span>
                  {g.id === garage.id && <Check size={13} />}
                </button>
              ))}
              <button
                role="menuitem"
                className="gsw-item gsw-new"
                onClick={() => {
                  setCreating(true);
                  setOpen(false);
                }}
              >
                <Plus size={13} /> New garage
              </button>
            </div>
          </>
        )}
      </div>

      {creating && <CreateGarage onClose={() => setCreating(false)} />}
    </>
  );
}

/**
 * Create a garage.
 *
 * Creating one makes you its owner in the same operation server-side — that
 * membership is what every later authorization check reads (routes/garages.ts),
 * so there is no separate "join your own garage" step to get wrong here.
 */
export function CreateGarage({ onClose, standalone }: { onClose?: () => void; standalone?: boolean }) {
  const [name, setName] = useState('');
  const create = useCreateGarage();
  const { select } = useGarageContext();

  const submit = () => {
    if (!name.trim() || create.isPending) return;
    create.mutate(
      { name: name.trim() },
      {
        onSuccess: (garage) => {
          // Switch to it — creating a garage and then not being in it would be
          // a strange place to leave someone.
          select(garage.id);
          onClose?.();
        },
      },
    );
  };

  // The API validates the name (1–80 chars) and reports per-field issues; show
  // its verdict on the field rather than inventing a second set of rules here.
  const fieldIssue = create.error instanceof ApiError ? create.error.issueFor('name') : undefined;

  return (
    <div className={standalone ? 'gsw-create-standalone' : 'sheet'}>
      <div className={standalone ? '' : 'sheet-body'}>
        {!standalone && (
          <div className="sheet-head">
            <button className="sheet-cancel" onClick={onClose}>
              Cancel
            </button>
            <div className="sheet-title">New garage</div>
            <span />
          </div>
        )}

        <div className="create-card">
          <span className="create-ic">
            <Users size={20} />
          </span>
          <b className="create-h">Name your garage</b>
          <span className="create-sub">
            A garage holds your cars, drivers and parts. You can invite crew to it later.
          </span>

          <input
            className="input"
            value={name}
            autoFocus
            maxLength={80}
            placeholder="e.g., The DigiCar Garage"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          {fieldIssue && <div className="field-err">{fieldIssue}</div>}
          {create.isError && !fieldIssue && <div className="field-err">{errorMessage(create.error)}</div>}

          <button className="sheet-save-lg" onClick={submit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create garage'}
          </button>
        </div>
      </div>
    </div>
  );
}
